/**
 * SupportEmailPage — OPEN-109. Two platform settings, changed here with no code change or
 * deploy (Corbin, 2026-10-13):
 *
 *   - Support email address — what users are shown (sign-in help line, documentation).
 *   - Forward support email to — where mail RECEIVED at support@lodexpro.com is forwarded.
 *     Empty = mail is kept (30 days) and nothing is forwarded.
 *
 * GET / PUT /v1/platform/support-settings. The API mirrors the forward-to address to the
 * forwarder's config; the screen shows a warning if that mirror ever disagrees with the saved
 * value ("forwarding out of sync"), and saving again repairs it.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'src/components/ui/button';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';

export interface SupportSettings {
  readonly persisted: boolean;
  readonly supportEmail: string | null;
  readonly effectiveSupportEmail: string;
  readonly forwardToEmail: string | null;
  readonly updatedAt: string | null;
  readonly forwarding: {
    readonly configured: boolean;
    readonly mirrored: string | null;
    readonly inSync: boolean;
  };
}

const QUERY_KEY = ['platform', 'support-settings'] as const;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** '' → null (clear); anything else trimmed and lowercased. */
export function normaliseEmail(v: string): string | null {
  const t = v.trim().toLowerCase();
  return t === '' ? null : t;
}

export function emailError(v: string, required: boolean): string | null {
  const n = normaliseEmail(v);
  if (n === null) return required ? 'Required' : null;
  return EMAIL_RE.test(n) ? null : 'Enter a valid email address';
}

export function SupportEmailPage(): React.JSX.Element {
  const opts = useApiOpts();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useApiQuery<SupportSettings>(
    opts,
    '/v1/platform/support-settings',
    { queryKey: QUERY_KEY },
  );
  const [support, setSupport] = React.useState('');
  const [forwardTo, setForwardTo] = React.useState('');

  React.useEffect(() => {
    if (data !== undefined) {
      setSupport(data.effectiveSupportEmail);
      setForwardTo(data.forwardToEmail ?? '');
    }
  }, [data]);

  const save = useApiMutation<
    { supportEmail: string | null; forwardToEmail: string | null },
    SupportSettings
  >(opts, '/v1/platform/support-settings', 'PUT', {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Saved. Takes effect immediately.');
    },
    onError: () => {
      toast.error('Could not save. Nothing was changed.');
    },
  });

  if (isLoading) return <p style={{ padding: '24px', ...subText }}>Loading…</p>;
  if (isError || data === undefined) {
    return (
      <p role="alert" style={{ padding: '24px', color: 'var(--color-danger)' }}>
        Could not load the support settings.
      </p>
    );
  }

  const supportErr = emailError(support, true);
  const forwardErr = emailError(forwardTo, false);
  const invalid = supportErr !== null || forwardErr !== null;

  return (
    <div style={{ padding: '24px', maxWidth: '640px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Support email</h2>
      <p style={{ ...subText, marginTop: '2px', marginBottom: '16px' }}>
        Changes apply immediately. No deploy needed.
      </p>

      {data.forwarding.configured && !data.forwarding.inSync && (
        <div role="alert" style={{ marginBottom: '16px' }} data-testid="forwarding-out-of-sync">
          <Pill tone="warning" label="Forwarding out of sync" />{' '}
          <span style={subText}>
            Mail is currently forwarded to {data.forwarding.mirrored ?? 'nobody'}, not the saved
            address. Save again to fix it.
          </span>
        </div>
      )}

      <form
        onSubmit={(e): void => {
          e.preventDefault();
          if (invalid) return;
          save.mutate({
            supportEmail: normaliseEmail(support),
            forwardToEmail: normaliseEmail(forwardTo),
          });
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
      >
        <FormRow
          label="Support email address"
          htmlFor="support-email"
          required
          hint="Shown to users on the sign-in screen and in help text."
        >
          <input
            id="support-email"
            type="email"
            value={support}
            onChange={(e): void => setSupport(e.target.value)}
            style={fieldStyle}
            aria-invalid={supportErr !== null}
          />
          {supportErr !== null && (
            <p role="alert" style={{ color: 'var(--color-danger)', fontSize: '12px' }}>
              {supportErr}
            </p>
          )}
        </FormRow>

        <FormRow
          label="Forward support email to"
          htmlFor="forward-to"
          hint="Mail sent to support@lodexpro.com is forwarded here, and replies go back to the sender. Leave empty to stop forwarding (mail is kept for 30 days)."
        >
          <input
            id="forward-to"
            type="email"
            value={forwardTo}
            onChange={(e): void => setForwardTo(e.target.value)}
            style={fieldStyle}
            aria-invalid={forwardErr !== null}
          />
          {forwardErr !== null && (
            <p role="alert" style={{ color: 'var(--color-danger)', fontSize: '12px' }}>
              {forwardErr}
            </p>
          )}
        </FormRow>

        <div>
          <Button type="submit" disabled={invalid || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </div>
  );
}
